import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient, withClientId } from "@/lib/queryClient";
import {
  createCustomer,
  deleteCustomer,
  fetchAssignableSalesReps,
  fetchCustomerById,
  fetchCustomers,
  updateCustomer,
} from "./api";
import type { Customer } from "@/types/customers";

export const CUSTOMERS_KEY = ["customers"] as const;

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"): paused offline
// mutations replay through the setMutationDefaults registrations below.
export const CUSTOMER_CREATE_KEY = ["outbox", "customers", "create"] as const;
export const CUSTOMER_UPDATE_KEY = ["outbox", "customers", "update"] as const;
export const CUSTOMER_DELETE_KEY = ["outbox", "customers", "delete"] as const;

export type CustomerCreateBody = Parameters<typeof createCustomer>[0] & { id?: string };

queryClient.setMutationDefaults(CUSTOMER_CREATE_KEY, { mutationFn: createCustomer });
queryClient.setMutationDefaults(CUSTOMER_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: Parameters<typeof updateCustomer>[1] }) =>
    updateCustomer(vars.id, vars.body),
});
queryClient.setMutationDefaults(CUSTOMER_DELETE_KEY, { mutationFn: deleteCustomer });

export const useCreateCustomer = () => {
  const qc = useQueryClient();
  const mutation = useMutation<Customer, Error, CustomerCreateBody & { id: string }>({
    mutationKey: CUSTOMER_CREATE_KEY,
    mutationFn: createCustomer,
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
  return {
    ...mutation,
    mutate: (body: CustomerCreateBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: CustomerCreateBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  };
};

export const useUpdateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: CUSTOMER_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: Parameters<typeof updateCustomer>[1] }) =>
      updateCustomer(vars.id, vars.body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      qc.invalidateQueries({ queryKey: ["customers", data.id] });
    },
  });
};

export const useDeleteCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: CUSTOMER_DELETE_KEY,
    mutationFn: deleteCustomer,
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
};

export const useCustomers = (q?: string) => {
  const key: [string, Record<string, unknown>] = ["customers", {}];
  if (q) key[1].q = q;
  return useQuery({
    queryKey: key,
    queryFn: () => fetchCustomers(q),
    // Keep the previous result visible while a new search loads instead of
    // flashing the skeleton on every debounced keystroke.
    placeholderData: (prev) => prev,
  });
};

export const useCustomer = (customerId: string) => {
  return useQuery({
    queryKey: ["customers", customerId],
    queryFn: () => fetchCustomerById(customerId),
    enabled: !!customerId,
  });
};

export const ASSIGNABLE_SALES_REPS_KEY = ["customers", "assignable-sales-reps"] as const;

export const useAssignableSalesReps = () => {
  return useQuery({
    queryKey: ASSIGNABLE_SALES_REPS_KEY,
    queryFn: fetchAssignableSalesReps,
  });
};
